// StationDetailLayout – detail stanice: sidebar menu (výběr stanice + reporty → NoDefinedLayout) + StationPanelDetail vpravo
import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { StationPanelDetail } from '../components/panels/StationPanelDetail'
import groupIcon from '../assets/icons/group.svg'
import { STATION_DETAIL_REPORT_MAP, STATION_DETAIL_MENU_ITEMS } from '../config/productRegistry'
import { Spinner } from '@heroui/react'
import { addLastStation } from '../hooks/useLastStations'
import { useStationStore } from '../store/stationStore'
import { useAuthStore, selectIsAnonymous } from '../store/useAuthStore'
import { useLoginModalStore } from '../store/useLoginModalStore'
import { SelectedStationBox } from '../components/panels/SelectedStationBox'
import { UserIcon } from '../components/auth/UserIcon'
import { OnlineModeToggle } from '../components/OnlineModeToggle'
import { useAllStations } from '../hooks/useAppData'
import { useTrackProduct } from '../hooks/useTrackProduct'

function StationCombobox({ onSelect }) {
  const allStations = useStationStore((s) => s.allStations)
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const listRef = useRef(null)
  const inputRef = useRef(null)

  const filtered = useCallback(() => {
    const q = inputValue.trim()
    if (!q) return []
    const normalize = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    const nq = normalize(q)
    return allStations.filter(s => {
      return normalize(s.gh_id).startsWith(nq) ||
             normalize(s.wsi).includes(nq) ||
             normalize(s.full_name || s.name).includes(nq)
    }).slice(0, 30)
  }, [inputValue, allStations])

  const results = filtered()

  const confirmSelect = (station) => {
    setInputValue('')
    setOpen(false)
    setActiveIndex(-1)
    onSelect(station)
  }

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = activeIndex >= 0 ? activeIndex : 0
      if (results[idx]) confirmSelect(results[idx])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const item = listRef.current.children[activeIndex]
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  return (
    <div className="relative mx-2 my-2">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        placeholder="GH_ID / WSI / název…"
        className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
        onChange={e => {
          setInputValue(e.target.value)
          setActiveIndex(-1)
          if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropdownStyle({ position: 'fixed', top: r.bottom + 2, left: r.left, width: 288 })
          }
          setOpen(true)
        }}
        onFocus={() => {
          if (inputValue.trim() && inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropdownStyle({ position: 'fixed', top: r.bottom + 2, left: r.left, width: 288 })
            setOpen(true)
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
      />
      {open && results.length > 0 && (
        <ul
          ref={listRef}
          style={dropdownStyle}
          className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto text-xs"
        >
          {results.map((s, i) => (
            <li
              key={s.gh_id}
              className={`px-2 py-1.5 cursor-pointer flex flex-col gap-0 ${i === activeIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
              onMouseDown={() => confirmSelect(s)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="font-mono text-gray-500">{s.gh_id}{s.wsi ? `, ${s.wsi}` : ''}</span>
              <span className="font-medium leading-tight">{s.full_name || s.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// REPORT_MAP a MENU_ITEMS jsou od doc/plans/product-registry-refactor.md (etapa 1) odvozené
// z centrálního registru — viz STATION_DETAIL_REPORT_MAP/STATION_DETAIL_MENU_ITEMS
// v src/config/productRegistry.js.
const REPORT_MAP = STATION_DETAIL_REPORT_MAP
const MENU_ITEMS = STATION_DETAIL_MENU_ITEMS

// Mapování skupiny menu → kategorie dat (viz doc/plans/deploy-variants-a-b-c.md D3) —
// server posílá visibleDataCategories už spočtené pro (role × varianta nasazení);
// skupina se zobrazí, jen když je její kategorie v tomto poli. Skupina bez záznamu
// (zatím nemapovaná na konkrétní obs_type/kategorii) se nefiltruje vůbec.
const GROUP_DATA_CATEGORY = {
  '10minutová data': '10M',
  'Hodinová data':   'HLY',
  'Denní data':      'DLY',
  'Měsíční data':    'MLY',
  'Roční data':      'YLY',
  'Jevy':            'DLY',
}

export function StationDetailLayout() {
  const { ghId, report } = useParams()
  const navigate = useNavigate()
  const token = useAuthStore((s) => s.token)
  const isAnon = useAuthStore(selectIsAnonymous)
  const visibleDataCategories = useAuthStore((s) => s.visibleDataCategories)
  const selectedStation = useStationStore((state) => state.selectedStation)
  const setSelectedStation = useStationStore((state) => state.setSelectedStation)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const menuItems = MENU_ITEMS.filter(g => {
    const cat = GROUP_DATA_CATEGORY[g.group]
    return !cat || visibleDataCategories.includes(cat)
  })

  useAllStations() // načte allStations do store pro combobox

  const handleStationSelect = (station) => {
    navigate(`/station/${station.gh_id}${report ? '/' + report : ''}`)
  }

  useEffect(() => {
    if (!ghId) return
    setLoading(true)
    setError(null)
    api.get(`/geography?gh_id=${ghId}&join=[observation]&observation.end_date=[group]`)
      .then(data => {
        if (data.length > 0) {
          setSelectedStation(data[0])
          addLastStation(data[0])
        } else {
          setSelectedStation(null)
          setError('Stanice nenalezena nebo není dostupná pro aktuálního uživatele')
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [ghId, token, setSelectedStation])

  const ReportComponent = report ? REPORT_MAP[report] : null

  // Reporty ze skupin skrytých podle visibleDataCategories — přímé URL na skrytý slug
  // dostane stejnou hlášku jako filtrované menu (viz GROUP_DATA_CATEGORY výše).
  const hiddenReportSlugs = new Set(
    MENU_ITEMS
      .filter(g => {
        const cat = GROUP_DATA_CATEGORY[g.group]
        return cat && !visibleDataCategories.includes(cat)
      })
      .flatMap(g => g.items.map(i => i.slug))
  )
  const reportBlocked = report && hiddenReportSlugs.has(report)

  // Sledování přístupů (doc/plans/usage-analytics-client.md, etapa K2) — jedno volání
  // pro všechny produkty detailu stanice. Posílá se, až když se produkt opravdu vykresluje:
  // stanice je načtená, report existuje v registru a není skrytý variantou nasazení.
  const trackedSlug = selectedStation && !loading && !reportBlocked && ReportComponent ? report : null
  useTrackProduct(trackedSlug, { gh_id: selectedStation?.gh_id })

  return (
    <div className="flex h-dvh overflow-hidden">

      {/* Sidebar */}
      <aside className="w-56 shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <button
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors flex-1 min-w-0"
            onClick={() => navigate('/')}
          >
            <span className="text-xl leading-none">🏠</span> Domů
          </button>
          <button
            className="flex items-center text-blue-600 hover:text-blue-700 transition-colors shrink-0"
            title="Skupina stanic"
            onClick={() => navigate('/station-group')}
          >
            <img src={groupIcon} className="w-6 h-6" alt="Skupina stanic" />
          </button>
          <OnlineModeToggle />
          <UserIcon variant="compact" />
        </div>
        {/* Header */}
        <div className="border-b border-gray-100">
          {/* Výběr stanice – combobox */}
          <div className="border-b border-gray-100 pb-1">
            <button
              className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full transition-colors"
              onClick={() => navigate('/select', { state: { preselectGhId: selectedStation?.gh_id } })}
            >
              🔍 Výběr stanice
            </button>
            <StationCombobox onSelect={handleStationSelect} />
            <SelectedStationBox station={selectedStation} />
          </div>
        </div>

        {/* Menu groups */}
        <nav className="flex-1 py-2">
          {menuItems.map(group => (
            <div key={group.group} className="mb-2">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {group.group}
              </div>
              {group.chips ? (
                <div className="px-3 flex flex-wrap gap-1">
                  {group.items.map(item => {
                    const isActive = item.slug && item.slug === report
                    return (
                      <button
                        key={item.label}
                        className={`px-2 py-0.5 rounded-full text-xs transition-colors border ${
                          isActive
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        onClick={() => navigate(item.slug ? `/station/${ghId}/${item.slug}` : '/wip')}
                      >
                        {item.label}
                      </button>
                    )
                  })}
                </div>
              ) : (
                group.items.map(item => {
                  const isActive = item.slug && item.slug === report
                  return (
                    <button
                      key={item.label}
                      className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={() => navigate(item.slug ? `/station/${ghId}/${item.slug}` : '/wip')}
                    >
                      {item.label}
                    </button>
                  )
                })
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full">
            <p className="text-red-500">{error}</p>
          </div>
        )}
        {selectedStation && !loading && reportBlocked && (
          <div className="p-8 max-w-lg mx-auto">
            <div className="border-2 border-dashed border-gray-300 rounded-xl bg-white flex flex-col items-center justify-center text-center gap-3 p-8">
              <p className="text-gray-600 font-medium">Tato data nejsou pro váš účet/instanci dostupná.</p>
              {isAnon && (
                <button
                  onClick={() => useLoginModalStore.getState().show()}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                  Přihlásit se
                </button>
              )}
            </div>
          </div>
        )}
        {selectedStation && !loading && !reportBlocked && ReportComponent && (
          <ReportComponent station={selectedStation} />
        )}
        {selectedStation && !loading && !reportBlocked && !ReportComponent && (
          <StationPanelDetail station={selectedStation} />
        )}
      </main>
    </div>
  )
}
